import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../../services/analytics';
import UserContext from '../../context/UserContext';
import { FAN_ITEMS, FanLayer, FanPlusGlyph, useFan } from '../chrome/CreateFan';

/**
 * The + cell at the head of the rail, and the three slabs that fan out of it.
 * Design handoff §3 (docs/plans/2026-09-portals-stories/handoff/README.md).
 *
 * The rail sits under the navbar, so the fan can only open downward and to the right. The
 * fan itself — scrim, travel, focus trap, the inverted face — is the shared `CreateFan`
 * (the bottom nav's HOST uses the same one, fanning the other way); the labels come from
 * its one list.
 */
export const itemsFor = (canUsePlanner) =>
  (canUsePlanner ? [FAN_ITEMS.portal, FAN_ITEMS.event, FAN_ITEMS.party] : [FAN_ITEMS.portal, FAN_ITEMS.event, FAN_ITEMS.club]);

export default function CreateRing({ size = 64, label = 'OPEN', signedIn = true, onOpenChange = null }) {
  const navigate = useNavigate();
  // Read the context rather than useUser(), which throws without a provider: this is a leaf
  // the app mounts in several shells and two suites render on its own. No provider reads as
  // "no planner", which is the safe side of the switch.
  const items = itemsFor(useContext(UserContext)?.canUsePlanner === true);
  const fan = useFan({ onOpenChange });
  const { mounted, expanded, reduced, buttonRef } = fan;

  // The handoff draws the fan at a 64px cell; desktop's is 72.
  const scale = size / 64;

  const toggle = () => {
    if (!signedIn) { navigate('/login?return=/featured'); return; }
    if (mounted) fan.close();
    else { fan.open(); trackEvent('create_ring_open'); }
  };

  const pick = (item) => {
    trackEvent('create_ring_pick', { option: item.key });
    fan.dismiss();
    navigate(item.to);
  };

  const faceStyle = (inverted) => ({
    position: 'absolute',
    inset: 2,
    borderRadius: '50%',
    border: `2px solid ${inverted ? '#FFFFFF' : '#000000'}`,
    background: inverted ? '#000000' : '#FFFFFF',
    color: inverted ? '#FFFFFF' : '#000000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 180ms linear, border-color 180ms linear, color 180ms linear',
  });

  return (
    <div className="relative flex-shrink-0" onKeyDown={fan.onKeyDownInFan}>
      <div style={{ width: size }}>
        <button
          ref={buttonRef}
          type="button"
          onClick={toggle}
          aria-expanded={mounted}
          aria-haspopup="menu"
          aria-label={mounted ? 'Close the create menu' : 'Start something'}
          // rounded-full + focus-visible: the ring has to follow the circle, and a mouse click
          // should not leave a square outline sitting around the +.
          className="relative block rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
          style={{ width: size, height: size }}
        >
          <span style={faceStyle(false)}>
            <FanPlusGlyph rotated={false} reduced={reduced} />
          </span>
        </button>

        <span
          className="mt-1.5 block truncate text-center font-mono font-bold uppercase text-black"
          style={{ fontSize: size >= 72 ? 10 : 9, letterSpacing: '0.05em', maxWidth: size + 8 }}
        >
          {label}
        </span>
      </div>

      <FanLayer
        fan={fan}
        items={items}
        origin="rail"
        scale={scale}
        anchorSize={size}
        onPick={pick}
        menuLabel="Start something"
        face={(
          <span style={faceStyle(true)}>
            <FanPlusGlyph rotated={expanded} reduced={reduced} />
          </span>
        )}
      />
    </div>
  );
}
